import LangLink from '../../../../components/common/LangLink';

// 12 partner logos in public/img/landing/partner_01.webp ... partner_12.webp
const PARTNER_LOGOS = Array.from({ length: 12 }, (_, i) => {
    const num = String(i + 1).padStart(2, '0');
    return `/img/landing/partner_${num}.webp`;
});

// Card displays a partner logo on white bg with rounded corners
const LogoCard = ({ src, alt }) => (
    <div className="bg-white rounded-[14px] flex items-center justify-center w-[180px] h-[100px] shrink-0 shadow-sm">
        <img src={src} alt={alt} className="max-w-[75%] max-h-[60%] object-contain" loading="lazy" />
    </div>
);

// Vertical scrolling column with seamless infinite loop (content duplicated 2x).
// Direction "up" = bottom→top, "down" = top→bottom.
const ScrollingColumn = ({ logos, direction }) => {
    const animClass = direction === 'up' ? 'partner-scroll-up' : 'partner-scroll-down';
    return (
        <div
            className="overflow-hidden h-full"
            style={{
                maskImage: 'linear-gradient(to bottom, transparent, black 8%, black 92%, transparent)',
                WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 8%, black 92%, transparent)',
            }}
        >
            <div className={`flex flex-col gap-4 ${animClass}`}>
                {/* Duplicate the list 2x for seamless loop */}
                {[...logos, ...logos].map((src, i) => (
                    <LogoCard key={i} src={src} alt={`Partner ${(i % logos.length) + 1}`} />
                ))}
            </div>
        </div>
    );
};

// Horizontal scrolling row with seamless infinite loop (content duplicated 2x).
// Direction "left" = right→left, "right" = left→right. Used in mobile layout.
const ScrollingRow = ({ logos, direction }) => {
    const animClass = direction === 'left' ? 'partner-scroll-left' : 'partner-scroll-right';
    return (
        <div
            className="overflow-hidden w-full"
            style={{
                maskImage: 'linear-gradient(to right, transparent, black 6%, black 94%, transparent)',
                WebkitMaskImage: 'linear-gradient(to right, transparent, black 6%, black 94%, transparent)',
            }}
        >
            <div className={`flex gap-3 ${animClass}`}>
                {/* Duplicate the list 2x for seamless loop */}
                {[...logos, ...logos].map((src, i) => (
                    <div
                        key={i}
                        className="bg-white rounded-[12px] flex items-center justify-center w-[120px] h-[70px] shrink-0 shadow-sm"
                    >
                        <img
                            src={src}
                            alt={`Partner ${(i % logos.length) + 1}`}
                            className="max-w-[75%] max-h-[60%] object-contain"
                            loading="lazy"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const PartnerSection = ({ language }) => {
    const fr = language === 'fr';

    // Split logos in 2 columns (1-6 left, 7-12 right) so columns show different logos at any moment
    const leftColLogos = PARTNER_LOGOS.slice(0, 6);
    const rightColLogos = PARTNER_LOGOS.slice(6, 12);

    return (
        <section className="relative bg-white dark:bg-[#0f0f0f] text-gray-900 dark:text-white py-20 md:py-28">
            <div className="max-w-[1151px] mx-auto px-6">
                {/* Card: outer wrapper holds linear gradient "stroke", inner div the radial fill */}
                <div
                    className="relative rounded-[20px] p-[2px]"
                    style={{
                        background:
                            'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.4) 100%)',
                    }}
                >
                    <div
                        className="relative rounded-[18px] overflow-hidden md:grid md:grid-cols-[1fr_auto] md:gap-0 p-8 md:p-12 md:min-h-[437px]"
                        style={{
                            // Exact Figma fill: radial #0061FE → #001230, centered upper-left
                            background:
                                'radial-gradient(ellipse at 25% 20%, #0061FE 0%, #001230 100%)',
                        }}
                    >
                        {/* MOBILE — 2 animated rows (top→left, bottom→right), logos ABOVE text */}
                        <div className="md:hidden flex flex-col gap-3 -mx-8 mb-8">
                            <ScrollingRow logos={leftColLogos} direction="left" />
                            <ScrollingRow logos={rightColLogos} direction="right" />
                        </div>

                        {/* LEFT (desktop) / BELOW (mobile) — text + CTA */}
                        <div className="flex flex-col justify-center max-w-[420px]">
                            <h2 className="font-['Rethink_Sans',sans-serif] font-normal text-white text-[28px] leading-[34px] tracking-[-1px] md:text-[32px] md:leading-[38px] mb-6">
                                {fr
                                    ? 'Vous êtes une école ou un instructeur de vol ?'
                                    : 'Are you a flight school or instructor?'}
                            </h2>
                            <p className="font-['Rethink_Sans',sans-serif] font-normal text-[15px] leading-[24px] text-white/80 mb-8">
                                {fr
                                    ? 'Aidez vos élèves à réussir leurs examens TC. Suivez leur évolution en temps réel et maximisez leurs chances de réussite.'
                                    : 'Help your students pass their TC exams. Track their progress in real time and maximize their chances of success.'}
                            </p>
                            <LangLink
                                to="/partnership"
                                className="self-center md:self-start inline-flex items-center justify-center text-center md:text-left bg-white text-[#0061fe] font-['Manrope',sans-serif] font-semibold text-[13px] px-[26px] py-[14px] rounded-full hover:bg-white/90 active:scale-[0.98] transition-all"
                                onClick={() => window.scrollTo(0, 0)}
                            >
                                {fr ? 'Découvrir le programme partenaire' : 'Discover the partner program'}
                            </LangLink>
                        </div>

                        {/* DESKTOP — 2 animated logo columns (vertical) */}
                        <div className="hidden md:flex gap-4 h-[437px]">
                            <ScrollingColumn logos={leftColLogos} direction="down" />
                            <ScrollingColumn logos={rightColLogos} direction="up" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PartnerSection;
